import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "antd";
import * as _ from "lodash";
import * as Sentry from "@sentry/react";

import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";

// import required modules
import { Pagination } from "swiper/modules";

import "./styles.css";

import { ModalProductDetail } from "../../featured-products/components/ModalProductDetail";
import {
  useListaComunasYMaquinasPorProductoNombre,
  useListaProductosDestacados,
} from "@/hooks/useProductos";
import { useListaEmpresasMutation } from "@/hooks/useEmpresas";
import { truncateText } from "@/lib";
import { formatMoney, getDistance } from "@/lib";
import { useAuth } from "@/_zustand/auth";

export const ProductosDestacados = () => {
  const [show, setShow] = useState(false);
  const { mutateAsync } = useListaComunasYMaquinasPorProductoNombre();
  const empresas = useListaEmpresasMutation();
  const { mutateAsync: empresasMutateAsync } = empresas;
  const [maquinas, setMaquinas] = useState([]);
  const [isLoadingMaquinas, setIsLoadingMaquinas] = useState(false);
  const maxLength = 100;
  let anchoImg = "100%";
  let altoImg = "100%";
  const productos = useListaProductosDestacados();
  const itemRef = useRef(null);
  const storeRef = useRef(null);
  const requestIdRef = useRef(0);
  const { userAddress } = useAuth();
  const DEFAULT_LAT = -33.43695899222021;
  const DEFAULT_LON = -70.63442929100255;

  useEffect(() => {
    empresasMutateAsync();
  }, [empresasMutateAsync]);

  // ⭐️ 1. Preparar la lista con la distancia calculada y ordenar
  const productosOrdenados = useMemo(() => {
    if (!productos.data || !Array.isArray(productos.data)) return [];

    const productosDetalleCompletos = [];

    productos.data.forEach((productoPadre) => {
      if (productoPadre.detalle && Array.isArray(productoPadre.detalle)) {
        productoPadre.detalle
          .filter((prodDetalle) => prodDetalle.vende_licor !== 1) // si quieres filtrar, opcional
          .forEach((prodDetalle) => {
            productosDetalleCompletos.push({
              ...prodDetalle,
              // Inclusión de propiedades del padre
              empresa_id: productoPadre.empresa_id,
              empresa_nombre: productoPadre.empresa_nombre,
              maquina_id: productoPadre.maquina_id,
              maquina_nombre: productoPadre.maquina_nombre,
              maquina_direccion: productoPadre.maquina_direccion,
              maquina_latitud: productoPadre.maquina_latitud,
              maquina_longitud: productoPadre.maquina_longitud,
              distancia: productoPadre._distancia,
            });
          });
      }
    });

    return productosDetalleCompletos;
  }, [productos.data]);

  const handleClick = async (item) => {
    if (!item) return;
    const requestId = ++requestIdRef.current;
    itemRef.current = item;
    storeRef.current =
      _.find(Array.isArray(empresas.data) ? empresas.data : [], {
        empresa_id: item.empresa_id,
      }) ?? null;
    setMaquinas([]);
    setIsLoadingMaquinas(true);
    setShow(true);
    try {
      const resp = await mutateAsync(item.producto_nombre);
      if (requestIdRef.current !== requestId) return;
      setMaquinas(Array.isArray(resp) ? resp : []);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      Sentry.captureException(err);
      setMaquinas(null);
    } finally {
      if (requestIdRef.current === requestId) setIsLoadingMaquinas(false);
    }
  };

  const ProductItem = () => {
    if (_.size(productos.data) == 0) {
      return (
        <h4 className="text-center">
          No se encontraron productos disponibles.
        </h4>
      );
    }
    if (_.size(productos.data) < 2)
      return productosOrdenados.map((item, index) => (
        <div
          key={`${item.producto_id}-${item.empresa_id}-${index}`}
          onClick={() => handleClick(item)}
          className="col-12 col-lg-4 mb-5"
        >
          <div
            id={`card_${item.producto_id + item.empresa_id}`}
            className="card border-0"
            style={{
              width: "100%",
              backgroundColor:
                "rgba(var(--bs-light-rgb),var(--bs-bg-opacity))!important",
            }}
          >
            <img
              className="border shadow rounded p-2 img-fluid"
              src={item.producto_foto}
              alt={item.producto_id}
              width={anchoImg}
              height={altoImg}
              style={{
                maxHeight: "287px",
                maxWidth: "345px",
                width: "345px",
                height: "287px",
              }}
            />

            <div className="card-body">
              <h6
                className="card-title text-decoration-none text-black montserrat-ultrabold"
                style={{ height: "40px" }}
              >
                {item.producto_nombre}
              </h6>
              <p
                className="montserrat-regular text-black text"
                style={{ height: "80px" }}
              >
                {truncateText(item.producto_descripcion, maxLength)}
              </p>
              <p>{item.genero_nombre}</p>
              <p>{item.marca_nombre}</p>
              <div className="d-flex align-items-center justify-content-between">
                <span className="text-black montserrat-ultrabold">
                  {formatMoney(item.producto_precio)}
                </span>
                <div className="btn border rounded-pill border-dark">
                  <i className="fa-solid fa-cart-shopping"></i> Agregar al
                  carrito
                </div>
              </div>
            </div>
          </div>
        </div>
      ));
    return (
      <>
        <Swiper
          slidesPerView={3}
          rows={2}
          spaceBetween={30}
          grabCursor={true}
          pagination={{
            clickable: true,
          }}
          modules={[Pagination]}
          className="mySwiper"
          breakpoints={{
            340: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 50,
            },
            1024: {
              slidesPerView: 6,
              spaceBetween: 20,
            },
          }}
        >
          {productosOrdenados &&
            productosOrdenados.map((item, index) => (
              <SwiperSlide key={index}>
                <div className="card-product" onClick={() => handleClick(item)}>
                  <div className="card-image-product">
                    <img src={item.producto_foto} alt={item.producto_id} />
                  </div>
                  <div className="card-content-product">
                    <h2>{item.producto_nombre}</h2>
                    <p className="price-product">
                      {" "}
                      {formatMoney(item.producto_precio)} CLP
                    </p>
                    <div className="location-product">
                      <i className="icon-location"></i>
                      {userAddress?.length === 2 ? (
                        <span className="text-location">
                          {getDistance(
                            item.maquina_latitud,
                            item.maquina_longitud,
                            userAddress[0],
                            userAddress[1],
                          )}{" "}
                          km - {item.maquina_direccion}
                        </span>
                      ) : (
                        <span className="text-location">
                          {getDistance(
                            item.maquina_latitud,
                            item.maquina_longitud,
                            DEFAULT_LAT,
                            DEFAULT_LON,
                          )}{" "}
                          km - {item.maquina_direccion}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
        </Swiper>
      </>
    );
  };

  if (_.size(productos.data) > 0)
    return (
      <div className="container-fluid">
        <section id="productosMasVendidos" className="container my-5">
          <ModalProductDetail
            show={show}
            setShow={setShow}
            item={itemRef}
            store={storeRef}
            machines={maquinas}
            isLoading={isLoadingMaquinas}
          />
          <div className="container my-5">
            <div className="row">
              <div className="col-12 d-flex justify-content-start align-items-center">
                <div className="col-10">
                  <h3 className="text-start prompt-bold text-black title-20 my-3">
                    <Link
                      to="/featured-products"
                      className="nav-link active mx-3"
                      aria-current="page"
                    >
                      Destacados
                    </Link>
                  </h3>
                </div>
                <div className="col-2 text-end">
                  {productos.data.length >= 6 ? (
                    <span style={{ textAlign: "right" }}>
                      <Link
                        to="/featured-products"
                        className="nav-link active mx-3"
                        aria-current="page"
                      >
                        Ver todos
                      </Link>
                    </span>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              <div className="row" style={{ height: "fit-content" }}>
                <Skeleton loading={productos.isLoading} active>
                  <ProductItem />
                </Skeleton>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
};
